import { accountingApi, type ProjectAdvanceRow, type ProjectBankStatementRow, type ProjectDocumentLineRow } from '@/lib/api/accounting';
import type { ExpenseEntry } from '@/lib/mock/expenses';

/** سرفصل‌ها حذف شده‌اند؛ غنی‌سازی از روی لینک حساب دیگر انجام نمی‌شود. */
export async function enrichLinesFromHeadingLinks(
  lines: ProjectDocumentLineRow[],
): Promise<ProjectDocumentLineRow[]> {
  return lines;
}

/** بارگذاری ردیف‌های سند حسابداری مرتبط با یک پروژه */
export async function fetchProjectAccountingLines(projectId: number): Promise<ProjectDocumentLineRow[]> {
  if (!projectId) return [];
  try {
    const lines = await accountingApi.projectDocumentLines(projectId);
    return enrichLinesFromHeadingLinks(Array.isArray(lines) ? lines : []);
  } catch {
    return [];
  }
}

/** مبلغ هزینهٔ پروژه از ردیف‌های حسابداری (ترجیحاً بدهکار) */
export function accountingLineExpenseAmount(line: ProjectDocumentLineRow): number {
  const debit = Number(line.debit ?? 0);
  const credit = Number(line.credit ?? 0);
  if (debit > 0) return debit;
  if (credit > 0) return credit;
  return Number(line.amount ?? 0);
}

export async function fetchProjectBankStatements(projectId: number): Promise<ProjectBankStatementRow[]> {
  if (!projectId) return [];
  try {
    const res = await accountingApi.projectBankStatements(projectId);
    return res.items || [];
  } catch {
    return [];
  }
}

export async function fetchProjectAdvances(projectId: number): Promise<ProjectAdvanceRow[]> {
  if (!projectId) return [];
  const res = await accountingApi.projectAdvances(projectId);
  return res.items || [];
}

export function bankStatementToExpenseEntry(row: ProjectBankStatementRow, projectId: number): ExpenseEntry {
  const debit = Number(row.debit ?? 0);
  const credit = Number(row.credit ?? 0);
  const beneficiaryKey = row.beneficiary_key || '';
  const typeTitle = row.expense_type_title || '';
  return {
    id: `bsi-${row.id}`,
    projectId,
    beneficiaryId: beneficiaryKey,
    beneficiary: row.counterparty_label || '—',
    expenseTypeId: row.expense_type_id != null ? String(row.expense_type_id) : '',
    amount: debit > 0 ? debit : credit,
    debit,
    credit,
    dateFa: row.transaction_date || '',
    source: 'bsi_bank',
    docNo: row.doc_no || '',
    accountTitle: typeTitle || (debit > 0 && beneficiaryKey.startsWith('employee-') ? 'پرداخت بانکی' : 'گردش بانک'),
    paymentMethod: 'بانک',
    description: row.description || row.notes || 'گردش بانک ثبت‌شده',
    recordingMode: 'paid',
  };
}

export function advanceToExpenseEntry(row: ProjectAdvanceRow, projectId: number): ExpenseEntry {
  const amount = Number(row.amount ?? 0);
  const paymentType = row.payment_type || 'advance';
  const typeLabel =
    row.expense_type_title ||
    row.payment_type_label ||
    (paymentType === 'on_account' ? 'علی‌الحساب' : paymentType === 'other' ? 'سایر پرداخت' : 'مساعده');
  const expenseTypeId =
    row.expense_type_id != null
      ? String(row.expense_type_id)
      : `advance:${paymentType}`;
  const fromBank = Boolean(row.bsi_statement_registration_id);
  return {
    id: `advance-${row.id}`,
    projectId,
    beneficiaryId: row.beneficiary_key || (row.employee_id ? `employee-${row.employee_id}` : ''),
    beneficiary: row.employee_name || '—',
    expenseTypeId,
    amount,
    debit: amount,
    credit: 0,
    dateFa: row.date_fa || '',
    source: fromBank ? 'bsi_bank' : 'advance',
    docNo: row.payroll_month || '',
    accountTitle: typeLabel,
    paymentMethod: fromBank ? 'بانک' : 'بانک',
    description: row.description || row.notes || typeLabel,
    recordingMode: 'paid',
  };
}
